// src/hooks/usePWA.ts
// Internal hook implementation — consumed via PWAProvider context, not directly.
import { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';
import {
    __pwa_updateSW,
    __pwa_needsRefresh,
    __pwa_listeners,
} from '../pwa-bridge';

const INSTALL_DISMISSED_KEY = 'pwa-install-dismissed';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface PWAState {
    /** True when a new SW is waiting and the user hasn't dismissed the toast */
    needsUpdate: boolean;
    /** True when navigator.onLine is false */
    isOffline: boolean;
    /** True when `beforeinstallprompt` has fired and install is possible */
    canInstall: boolean;
    /** True when user has permanently dismissed the install banner */
    installDismissed: boolean;
    /** Activate the waiting service worker and reload */
    updateApp: () => void;
    /** Hide the update toast until next page load */
    dismissUpdate: () => void;
    /** Trigger the browser's native install prompt */
    installApp: () => void;
    /** Permanently dismiss the install banner (localStorage) */
    dismissInstallBanner: () => void;
}

export function usePWAInternal(): PWAState {
    const [needsUpdate, setNeedsUpdate] = useState(__pwa_needsRefresh);
    const [updateDismissed, setUpdateDismissed] = useState(false);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [installDismissed, setInstallDismissed] = useState(
        () => localStorage.getItem(INSTALL_DISMISSED_KEY) === 'true'
    );

    // Subscribe to SW needsRefresh changes from main.tsx
    useEffect(() => {
        const listener = () => setNeedsUpdate(true);
        __pwa_listeners.add(listener);
        return () => { __pwa_listeners.delete(listener); };
    }, []);

    // Online / Offline listeners
    useEffect(() => {
        const goOffline = () => setIsOffline(true);
        const goOnline = () => setIsOffline(false);
        window.addEventListener('offline', goOffline, { passive: true });
        window.addEventListener('online', goOnline, { passive: true });
        return () => {
            window.removeEventListener('offline', goOffline);
            window.removeEventListener('online', goOnline);
        };
    }, []);

    // Capture beforeinstallprompt
    useEffect(() => {
        const handler = (e: Event) => {
            e.preventDefault();
            setInstallPrompt(e as BeforeInstallPromptEvent);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const updateApp = useCallback(() => {
        setUpdateDismissed(true);
        if (__pwa_updateSW) {
            void __pwa_updateSW(true); // reloadPage = true
        }
    }, []);

    const dismissUpdate = useCallback(() => {
        setUpdateDismissed(true);
    }, []);

    const promptInstall = useCallback(async () => {
        if (!installPrompt) return;
        await installPrompt.prompt();
        const choice = await installPrompt.userChoice;
        if (choice.outcome === 'accepted') {
            logger.pwa('App installed');
        }
        setInstallPrompt(null);
    }, [installPrompt]);

    const installApp = useCallback(() => {
        void promptInstall();
    }, [promptInstall]);

    const dismissInstallBanner = useCallback(() => {
        localStorage.setItem(INSTALL_DISMISSED_KEY, 'true');
        setInstallDismissed(true);
    }, []);

    return {
        needsUpdate: needsUpdate && !updateDismissed,
        isOffline,
        canInstall: installPrompt !== null,
        installDismissed,
        updateApp,
        dismissUpdate,
        installApp,
        dismissInstallBanner,
    };
}
